import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  ArrowLeft, Search, Mail, Phone, Building2, Trash2, Loader2, Filter,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-[#FF9500]/15 text-[#B25E00] ring-1 ring-[#FF9500]/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-[#002FA7]/10 text-[#002FA7] ring-1 ring-[#002FA7]/30' },
  { value: 'qualified', label: 'Qualified', color: 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' },
  { value: 'won', label: 'Won', color: 'bg-[#34C759]/15 text-[#117A33] ring-1 ring-[#34C759]/40' },
  { value: 'lost', label: 'Lost', color: 'bg-slate-200 text-slate-700 ring-1 ring-slate-300' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/demo-requests`, { withCredentials: true });
      setLeads(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`${API}/demo-requests/${id}`, { status }, { withCredentials: true });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/demo-requests/${id}`, { withCredentials: true });
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (filter !== 'all' && (l.status || 'new') !== filter) return false;
      if (query) {
        const q = query.toLowerCase();
        return [l.name, l.email, l.company].some((v) => (v || '').toLowerCase().includes(q));
      }
      return true;
    });
  }, [leads, filter, query]);

  const countsByStatus = useMemo(() => {
    const m = { all: leads.length };
    STATUS_OPTIONS.forEach((s) => { m[s.value] = 0; });
    leads.forEach((l) => { const s = l.status || 'new'; m[s] = (m[s] || 0) + 1; });
    return m;
  }, [leads]);

  if (loading) return <DashboardLayout><div className="p-8 flex items-center gap-2 text-slate-600"><Loader2 className="w-4 h-4 animate-spin" /> Loading leads…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto" data-testid="leads-page">
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="leads-back-button"
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors text-sm"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Demo Leads
            </h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Track requests from your landing page. Move them through the funnel: New → Contacted → Qualified → Won/Lost.
            </p>
          </div>
        </div>

        {error && <div data-testid="leads-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

        {/* Filter pills */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mr-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Filter className="w-3.5 h-3.5" /> Filter
          </div>
          {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map((opt) => (
            <button
              key={opt.value}
              data-testid={`leads-filter-${opt.value}`}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === opt.value ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-400'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {opt.label} <span className="ml-1 opacity-70">{countsByStatus[opt.value] || 0}</span>
            </button>
          ))}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="leads-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, company, email…"
              className="pl-9 pr-4 py-2 text-sm w-72 border border-slate-200 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                {['Lead', 'Contact', 'Fleet size', 'Status', 'Received', ''].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-16 text-center text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No leads match your filters.</td></tr>
              )}
              {filtered.map((lead) => {
                const statusOpt = STATUS_OPTIONS.find((s) => s.value === (lead.status || 'new')) || STATUS_OPTIONS[0];
                return (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                    <td className="py-4 px-4">
                      <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.name || '—'}</div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <Building2 className="w-3 h-3" /> {lead.company || '—'}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#002FA7]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <Mail className="w-3.5 h-3.5" /> {lead.email}
                      </a>
                      {lead.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-4 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{lead.fleet_size || '—'}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusOpt.color}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {statusOpt.label}
                        </span>
                        <select
                          data-testid={`lead-status-${lead.id}`}
                          disabled={busyId === lead.id}
                          value={lead.status || 'new'}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          className="text-xs border border-slate-200 rounded-md px-2 py-1 focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white disabled:opacity-50"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{fmtDate(lead.created_at)}</td>
                    <td className="py-4 px-4">
                      <button
                        data-testid={`lead-delete-${lead.id}`}
                        onClick={() => removeLead(lead.id)}
                        disabled={busyId === lead.id}
                        className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Leads;
